export type BlockState = Record<string, string>;

export interface BlockData {
  blockId: string;
  state: BlockState;
}

export type BlockSpecInput =
  | string
  | {
      blockId?: string;
      id?: string;
      state?: Record<string, unknown>;
    }
  | null
  | undefined;

export function parseBlockSpec(
  input: BlockSpecInput,
  explicitState: Record<string, unknown> = {}
): BlockData | null {
  if (input && typeof input === "object") {
    return normalizeBlockData({
      blockId: input.blockId ?? input.id,
      state: input.state ?? {}
    });
  }

  const raw = String(input ?? "").trim();
  if (!raw || raw === "air" || raw === "minecraft:air") {
    return null;
  }

  const bracketStart = raw.indexOf("[");
  const rawBlockId = bracketStart >= 0 ? raw.slice(0, bracketStart) : raw;
  const blockId = rawBlockId.replace(/^minecraft:/, "");
  const state: BlockState = {};

  if (bracketStart >= 0 && raw.endsWith("]")) {
    const stateText = raw.slice(bracketStart + 1, -1);
    stateText.split(",").forEach((pair) => {
      const separator = pair.indexOf("=");
      if (separator <= 0) return;

      const key = pair.slice(0, separator).trim();
      const value = pair.slice(separator + 1).trim();
      if (key) state[key] = value;
    });
  }

  Object.entries(explicitState).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      state[key] = String(value);
    }
  });

  return normalizeBlockData({ blockId, state });
}

export function normalizeBlockData(
  blockData:
    | {
        blockId?: unknown;
        state?: Record<string, unknown>;
      }
    | null
    | undefined
): BlockData | null {
  if (!blockData?.blockId || blockData.blockId === "air") {
    return null;
  }

  const normalizedState: BlockState = {};
  Object.keys(blockData.state ?? {})
    .sort()
    .forEach((key) => {
      const value = blockData.state?.[key];
      if (value !== undefined && value !== null && value !== "") {
        normalizedState[key] = String(value);
      }
    });

  return {
    blockId: String(blockData.blockId).replace(/^minecraft:/, ""),
    state: normalizedState
  };
}

export function serializeBlockSpec(
  input: BlockSpecInput,
  explicitState: Record<string, unknown> = {}
): string | null {
  const blockData = parseBlockSpec(input, explicitState);
  if (!blockData) return null;

  const pairs = Object.entries(blockData.state);
  if (pairs.length === 0) return blockData.blockId;

  return (
    blockData.blockId +
    "[" +
    pairs.map(([key, value]) => key + "=" + value).join(",") +
    "]"
  );
}

export function toMinecraftBlockSpec(
  input: BlockSpecInput,
  explicitState: Record<string, unknown> = {}
): string {
  const blockData = parseBlockSpec(input, explicitState);
  if (!blockData) return "minecraft:air";

  const canonical = serializeBlockSpec(blockData)!;
  const bracketStart = canonical.indexOf("[");

  if (bracketStart < 0) {
    return "minecraft:" + canonical;
  }

  return (
    "minecraft:" +
    canonical.slice(0, bracketStart) +
    canonical.slice(bracketStart)
  );
}

export function blockSpecsEqual(
  first: BlockSpecInput,
  second: BlockSpecInput
): boolean {
  return serializeBlockSpec(first) === serializeBlockSpec(second);
}

export function stateKey(state: Record<string, unknown> = {}): string {
  return Object.keys(state)
    .sort()
    .map((key) => key + "=" + String(state[key]))
    .join(",");
}
